import { motion } from 'framer-motion';
import { CheckCircle, Clock, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

const benefits = [
  'Permanent residency',
  'Work-life balance',
  'Beautiful natural environment',
  'Free healthcare',
  'Quality education',
  'Safe and peaceful',
];

const NewZealandPRPage = () => {
  usePageMeta(
    'New Zealand Skilled Migrant PR from India 2026 | Job Placement — Zafco HR (MEA Approved)',
    'New Zealand Skilled Migrant Category PR with Zafco HR. Green List occupations, work-to-residence pathway, family inclusion, healthcare access. MEA-approved consultants since 2015.'
  );
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20New%20Zealand%20PR', '_blank');
  };

  return (
    <div data-testid="nz-pr-page">
      <PageHeader 
        title="New Zealand PR Job Placement 2026"
        subtitle="Skilled migration opportunities in the land of opportunities"
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'New Zealand', path: '#' },
          { label: 'NZ PR' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1700890626283-fa36c0ec5c5c?w=1920"
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                New Zealand Skilled Worker Program
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                New Zealand PR Job Placement
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                New Zealand offers excellent quality of life and career opportunities for skilled 
                professionals. The points-based Skilled Migrant Category provides a pathway to 
                permanent residency for qualified candidates.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 mb-8">
                <Clock className="w-6 h-6 text-zafco-teal mb-2" />
                <p className="text-2xl font-bold text-zafco-blue">6-12 Months</p>
                <p className="text-sm text-gray-600">Processing Time</p>
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Check Eligibility
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free NZ Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6">
            Start Your NZ Journey Today
          </h2>
          <Button 
            onClick={openWhatsApp}
            size="lg"
            className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
          >
            <Phone className="w-6 h-6 mr-3" />
            Get Free Assessment
          </Button>
        </div>
      </section>
    </div>
  );
};

export default NewZealandPRPage;
